import { Box, InputAdornment, LinearProgress, TextField, Typography, useTheme } from "@mui/material";
import { ListTodo, Search, X } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { getKillLaunchCountdown } from "@/features/kill-initiation";
import type { DetectiveSidebarBadge } from "@/features/detective-agency";
import { getVaultPayoutCountdown } from "@/features/vault";
import { useTermsDialog } from "@/features/terms";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import {
  selectJailedUntil,
  selectNextBustOutTime,
} from "@/features/jail";
import {
  selectNextSafehouseTime,
  selectSafehouseUntil,
} from "@/features/safehouse";
import { City, GAME_CASH_TRANSFER_COOLDOWN } from "../../constants/const";
import { JAIL_RESTRICTED_PATHS, SAFEHOUSE_RESTRICTED_PATHS } from "../../constants/jail";
import { SideNavLinks } from "../../constants/page";
import useCurrentTime from "../../hook/useCurrentTime";
import useWallet from "../../hook/useWallet";
import {
  getUserHelperBotInfo
} from "@/features/helper-bot";
import { ProfileNames } from "../../types/ProfileName";
import useLotteryHallSidebar from "../../hook/useLotteryHallSidebar";
import {
  convertDateTimeWithCooldown,
  convertDateTimeWithMinutes,
  formatNumberWithComma,
  toUSDFormat,
} from "../../utils/utils";
import millify from "millify";
import { useStyles } from "./SideBar.styles";
import { selectNextRaceTime } from "@/features/race";
import { selectNextTrainTime } from "@/features/kill-skill";
import { selectNextCrimeTime } from "@/features/crime";
import { selectNextNickTime } from "@/features/nick-car";
import { selectNextCrushTime } from "@/features/car-crusher";
import {
  selectStoryModeProgress,
  selectWeeklyMissionSummary,
} from "@/features/story";

const SideBar = ({ detectiveBadge }: { detectiveBadge: DetectiveSidebarBadge }) => {
  const { classes } = useStyles();
  const theme = useTheme();
  const currentTime = useCurrentTime();
  const dispatch = useAppDispatch();
  const { account } = useWallet();
  const { openTerms } = useTermsDialog();
  const [searchQuery, setSearchQuery] = useState("");
  const searchRef = useRef<HTMLDivElement>(null);
  const { info: lotterySidebar } = useLotteryHallSidebar();

  const { myProfile, profileNames } = useAppSelector(
    (state) => state.profile
  );
  const { userTravelInfo } = useAppSelector((state) => state.travel);

  const lastTransferTime = useAppSelector((state) => state.bank.lastTransferTime);

  const {
    userCarBotInfo,
    userBoozeBotInfo,
    userCrimeBotInfo,
    userKSBotInfo,
    userNarcsBotInfo,
    userBulletBotInfo
  } = useAppSelector((state) => state.helperBot);

  const nextHealthBuyTime = useAppSelector(
    (state) => state.hospital.nextHealthBuyTime
  );

  const nextBulletBuyTime = useAppSelector(
    (state) => state.bulletFactory.nextBulletBuyTime
  );

  const { nextNarcsTime, nextBoozeTime } = useAppSelector(
    (state) => state.smuggling
  );

  const nextRaceTime = useAppSelector(selectNextRaceTime);
  const nextTrainTime = useAppSelector(selectNextTrainTime);
  const nextCrimeTime = useAppSelector(selectNextCrimeTime);
  const nextNickTime = useAppSelector(selectNextNickTime);
  const nextCrushTime = useAppSelector(selectNextCrushTime);
  const jailedUntil = useAppSelector(selectJailedUntil);
  const nextBustOutTime = useAppSelector(selectNextBustOutTime);
  const storyModeProgress = useAppSelector(selectStoryModeProgress);
  const { status: weeklyMissionStatus, progress: weeklyMissionProgress } =
    useAppSelector(selectWeeklyMissionSummary);

  const { nextLobbyTime } = useAppSelector((state) => state.organizedCrime);

  const nextSafehouseEnterTime = useAppSelector(selectNextSafehouseTime);
  const safehouseUntil = useAppSelector(selectSafehouseUntil);

  const { currentRound } = useAppSelector((state) => state.jackpot);

  const { nextBuyTime } = useAppSelector((state) => state.shop);

  const remainTimes: Record<string, any[]> = useMemo(() => {
    return {
      Crimes: [
        nextCrimeTime > currentTime
          ? convertDateTimeWithMinutes(nextCrimeTime - currentTime)
          : "Now",
        userCrimeBotInfo.isRunning ? "Bot" : "",
      ],

      "Nick a car": [
        nextNickTime > currentTime
          ? convertDateTimeWithMinutes(nextNickTime - currentTime)
          : "Now",
        userCarBotInfo.isRunning ? "Bot" : "",
      ],
      Travel: [
        userTravelInfo
          ? userTravelInfo?.travelUntil > currentTime
            ? convertDateTimeWithMinutes(
              userTravelInfo?.travelUntil - currentTime
            )
            : "Now"
          : "Now",
      ],
      Jail: [
        jailedUntil > currentTime || nextBustOutTime > currentTime
          ? convertDateTimeWithMinutes(
            Math.max(jailedUntil - currentTime, nextBustOutTime - currentTime)
          )
          : "Now",
      ],
      "Helper bots": [""],
      "Kill skill": [
        nextTrainTime > currentTime
          ? convertDateTimeWithMinutes(nextTrainTime - currentTime)
          : "Now",
        userKSBotInfo.isRunning ? "Bot" : "",
      ],
      "Kill initiation": [getKillLaunchCountdown(currentTime * 1000).compact],
      "Detective Agency": [detectiveBadge ?? ""],
      "Vault countdown": [getVaultPayoutCountdown(currentTime * 1000)],
      Narcotics: [
        nextNarcsTime > currentTime
          ? convertDateTimeWithMinutes(nextNarcsTime - currentTime)
          : "Now",
        userNarcsBotInfo.isRunning ? "Bot" : "",
      ],
      Booze: [
        nextBoozeTime > currentTime
          ? convertDateTimeWithMinutes(nextBoozeTime - currentTime)
          : "Now",
        userBoozeBotInfo.isRunning ? "Bot" : "",
      ],
      "Bullet Factory": [
        nextBulletBuyTime > currentTime
          ? convertDateTimeWithMinutes(nextBulletBuyTime - currentTime)
          : "Now",
        userBulletBotInfo.isRunning ? "Bot" : "",
      ],
      Bank: [
        lastTransferTime + GAME_CASH_TRANSFER_COOLDOWN > currentTime
          ? convertDateTimeWithMinutes(
            lastTransferTime + GAME_CASH_TRANSFER_COOLDOWN - currentTime
          )
          : "Now",
        "",
      ],
      Hospital: [
        nextHealthBuyTime > currentTime
          ? convertDateTimeWithMinutes(nextHealthBuyTime - currentTime)
          : "Now",
        "",
      ],
      Racing: [
        nextRaceTime > currentTime
          ? convertDateTimeWithMinutes(nextRaceTime - currentTime)
          : "Now",
        "",
      ],
      Jackpot: [
        currentRound.liveTime + currentRound.duration > currentTime ||
          currentRound.state === 2 ? (
          <span style={{ color: theme.design.color.status.danger }}>
            {"$" +
              toUSDFormat(currentRound.totalUSD) +
              " " +
              convertDateTimeWithMinutes(
                currentRound.liveTime + currentRound.duration - currentTime > 0
                  ? currentRound.liveTime + currentRound.duration - currentTime
                  : 0
              )}
          </span>
        ) : currentRound.entriesCount >= 1 ? (
          <span style={{ color: theme.design.color.status.warning }}>
            {"$" + toUSDFormat(currentRound.totalUSD) + "  Waiting"}
          </span>
        ) : (
          "Now"
        ),
        "",
      ],
      "Lottery": [
        lotterySidebar.roundId ? (
          lotterySidebar.endTime > 0 ? (
            <span style={{ color: theme.design.color.status.warning }}>
              {millify(Math.floor(lotterySidebar.entriesCash || 0), { precision: 2 }) + " cash"}
            </span>
          ) : (
            <span style={{ color: theme.design.color.status.warning }}>
              {millify(Math.floor(lotterySidebar.entriesCash || 0), { precision: 2 }) +
                " cash  Waiting"}
            </span>
          )
        ) : (
          "Now"
        ),
        "",
      ],
      Shop: [
        nextBuyTime > currentTime
          ? convertDateTimeWithMinutes(nextBuyTime - currentTime)
          : "Now",
        "",
      ],
      "Organized Crime": [
        nextLobbyTime > currentTime
          ? convertDateTimeWithMinutes(nextLobbyTime - currentTime)
          : "Now",
        "",
      ],
      "Car Crusher": [
        nextCrushTime > currentTime
          ? convertDateTimeWithMinutes(nextCrushTime - currentTime)
          : "Now",
        "",
      ],
      Safehouse: [
        nextSafehouseEnterTime > 0 && nextSafehouseEnterTime > currentTime
          ? convertDateTimeWithMinutes(nextSafehouseEnterTime - currentTime)
          : "Now",
        "",
      ],
    };
  }, [
    currentTime,
    detectiveBadge,
    userCrimeBotInfo.isRunning,
    nextNickTime,
    userCarBotInfo.isRunning,
    userTravelInfo,
    jailedUntil,
    nextBustOutTime,
    nextTrainTime,
    userKSBotInfo.isRunning,
    nextNarcsTime,
    userNarcsBotInfo.isRunning,
    nextBoozeTime,
    userBoozeBotInfo.isRunning,
    userBulletBotInfo.isRunning,
    lastTransferTime,
    currentRound.liveTime,
    currentRound.duration,
    currentRound.state,
    currentRound.totalUSD,
    currentRound.entriesCount,
    lotterySidebar.roundId,
    lotterySidebar.endTime,
    lotterySidebar.entriesCash,
    nextBuyTime,
    nextLobbyTime,
    nextCrushTime,
    nextSafehouseEnterTime,
  ]);

  // Filter profile names based on search query
  const filteredProfileNames = useMemo(() => {
    if (!searchQuery.trim() || !profileNames) {
      return [];
    }

    const query = searchQuery.toLowerCase().trim();
    return profileNames.filter(
      (profile: ProfileNames) =>
        profile.name && profile.name.toLowerCase().includes(query)
    );
  }, [searchQuery, profileNames]);

  const isInJail = jailedUntil > currentTime;
  const isInSafehouse = safehouseUntil > 0 && currentTime < safehouseUntil;

  useEffect(() => {
    if (!account) return;

    dispatch(getUserHelperBotInfo(account));
  }, [account, dispatch]);

  // Handle click outside to dismiss dropdown and clear search
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        searchRef.current &&
        !searchRef.current.contains(event.target as Node)
      ) {
        setSearchQuery("");
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const weeklyMissionRemainingTime = useMemo(() => {
    return weeklyMissionStatus.startedAt + 7 * 24 * 60 * 60 - currentTime;
  }, [weeklyMissionStatus.startedAt, currentTime]);

  return (
    <Box className={classes.sideBar}>
      {/* Search Bar */}
      <Box
        className={classes.searchContainer}
        sx={{ position: "relative" }}
        ref={searchRef}
      >
        <TextField
          placeholder="Search Profile ..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className={classes.searchInput}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search
                  color={theme.design.color.text.primary}
                  size={16}
                  style={{ opacity: 0.7 }}
                />
              </InputAdornment>
            ),
            endAdornment: searchQuery && (
              <InputAdornment position="end">
                <X
                  onClick={() => setSearchQuery("")}
                  color={theme.design.color.text.primary}
                  size={16}
                  style={{ cursor: "pointer", opacity: 0.7 }}
                />
              </InputAdornment>
            ),
          }}
        />

        {/* Profile Names Dropdown */}
        {filteredProfileNames.length > 0 && (
          <Box className={classes.profileDropdown}>
            {filteredProfileNames.map((profile, index) => (
              <Link
                to={`/profile/${profile.name}`}
                key={index}
                className={classes.profileDropdownItem}
              >
                {profile.name}
              </Link>
            ))}
          </Box>
        )}
      </Box>

      <Box className={classes.navLinkGroup}>
        <Box className={classes.navLinkHeader}>
          <Box className={classes.navLinkHeaderContent}>
            <Box
              component={"img"}
              src="/assets/imgs/layout/tasklist.png"
              className={classes.navLinkIcon}
            />
            <Typography className={classes.navLinkText}>
              Tasks
            </Typography>
          </Box>

          <Box className={classes.navLinkHeaderLine}></Box>
        </Box>

        <Box className={classes.navLinks}>
          <Link to="/story" className={classes.navLink}>
            <Typography className={classes.navLinkText} sx={{ minWidth: "85px" }}>Story mode</Typography>

            <LinearProgress
              variant="determinate"
              value={storyModeProgress}
              sx={{
                height: "6px",
                borderRadius: 8,
                width: "100%",
                backgroundColor: theme.design.color.surface.control,
                "& .MuiLinearProgress-bar": {
                  backgroundColor: theme.design.color.action.primary,
                },
              }}
            />
            <Typography sx={{ fontSize: "10px!important", color: theme.design.color.text.primary, fontWeight: 900, whiteSpace: "nowrap", marginRight: "8px" }}>{storyModeProgress}%</Typography>
          </Link>
          <Link to="/weekly" className={classes.navLink}>
            <Typography className={classes.navLinkText} sx={{ minWidth: "85px" }}>Missions</Typography>

            <LinearProgress
              variant="determinate"
              value={weeklyMissionProgress}
              sx={{
                height: "6px",
                borderRadius: 8,
                width: "100%",
                backgroundColor: theme.design.color.surface.control,
                "& .MuiLinearProgress-bar": {
                  backgroundColor: theme.design.color.status.warning,
                },
              }}
            />
            <Typography sx={{ fontSize: "8px!important", color: theme.design.color.text.primary, fontWeight: 900, whiteSpace: "nowrap", marginRight: "8px" }}>
              {weeklyMissionRemainingTime > 0 ? convertDateTimeWithCooldown(weeklyMissionRemainingTime) : "Expired"}
            </Typography>
          </Link>
        </Box>
      </Box>

      {SideNavLinks.map((navLinks, index) => {
        return (
          <Box className={classes.navLinkGroup} key={index}>
            <Box className={classes.navLinkHeader}>
              <Box className={classes.navLinkHeaderContent}>
                <Box
                  component={"img"}
                  src={navLinks.icon}
                  className={classes.navLinkIcon}
                />
                <Typography className={classes.navLinkText}>
                  {navLinks.category === "LOCATION"
                    ? myProfile.city !== undefined
                      ? City[myProfile.city]
                      : "LOCATION"
                    : navLinks.category}
                </Typography>
              </Box>

              <Box className={classes.navLinkHeaderLine}></Box>
            </Box>

            <Box className={classes.navLinks}>
              {navLinks.subLinks.map((link, linkIndex) => {
                if ("action" in link && link.action === "terms") {
                  return (
                    <button
                      type="button"
                      className={`${classes.navLink} ${classes.navAction}`}
                      key={linkIndex}
                      onClick={openTerms}
                    >
                      <Typography component="span" sx={{ whiteSpace: "nowrap" }}>{link.name}</Typography>
                    </button>
                  );
                }
                const linkPath = navLinks.category === "LOCATION"
                  ? (link as any).needCityId ? link.link + `/${myProfile.city || 0}` : link.link
                  : link.name === "My profile"
                    ? link.link + `/${myProfile.name}`
                    : link.link;
                const isJailRestricted = isInJail && JAIL_RESTRICTED_PATHS.some((path) => linkPath.includes(path));
                const isSafehouseRestricted = isInSafehouse && SAFEHOUSE_RESTRICTED_PATHS.some((path) => linkPath.includes(path));
                const isLinkRestricted = isJailRestricted || isSafehouseRestricted;
                const redirectTo = isJailRestricted ? "/injail" : isSafehouseRestricted ? "/safehouse" : linkPath;

                return (
                  <Link
                    to={redirectTo}
                    target={(link as any).target ?? "_self"}
                    className={classes.navLink}
                    key={linkIndex}
                  >
                    <Typography sx={{ whiteSpace: "nowrap" }}>{link.name}</Typography>
                    <Box className={classes.badgeList}>
                      {(link as any).showCooldown &&
                        remainTimes[link.name] &&
                        remainTimes[link.name].map((time, index) => {
                          return time ? (
                            <Box
                              key={index}
                              className={
                                link.name === "Kill initiation" ||
                                (link.name === "Detective Agency" && time === "FOUND")
                                  ? classes.killCountdownBadge
                                  : link.name === "Vault countdown" ||
                                    (link.name === "Detective Agency" && time === "SEARCHING")
                                  ? classes.vaultCountdownBadge
                                  : time === "Bot"
                                  ? classes.botBadge
                                  : time === "Now"
                                    ? classes.nowBadge
                                    : classes.timeBadge
                              }
                              sx={{
                                color:
                                  (link.name === "Jail" ||
                                    link.name === "Jackpot") &&
                                    time !== "Now" &&
                                    time !== "Waiting"
                                    ? `${theme.design.color.status.danger}!important`
                                    : "",
                              }}
                            >
                              {time ?? "Now"}
                            </Box>
                          ) : (
                            ""
                          );
                        })}
                    </Box>
                  </Link>
                );
              })}
            </Box>
          </Box>
        );
      })}
    </Box>
  );
};

export default SideBar;
